import React, { useMemo, useState, useEffect } from 'react';
import { Modal } from '../ui/Modal/Modal';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
import { Dialog } from '../ui/Modal/Modal';
import { Card, CardContent } from '../ui/Card/Card';
import { SimpleTooltip } from '../ui/Tooltip/Tooltip';
import { useNotificationStore } from '../../stores/notificationStore';
import { AttachmentsService } from '../../services/AttachmentsService';
import { AttachmentPreviewModal } from './AttachmentPreviewModal';
import { Table } from '../ui/Table/Table';
import { TableActionButton, TableActions } from '../ui';
import { Eye, ExternalLink, Trash2 } from 'lucide-react';
import { formatDateTimeStr } from '@/lib/localeFormatters';

const attachmentsService = new AttachmentsService();

// AI-powered attachment analysis (auto-description of uploads) is disabled —
// see changelog 1.0.28. The backend worker, DB columns, and manual /analyze
// route are all preserved. Flip this flag to true to re-expose the
// "Analyzing..." spinner and the "🤖 Analyze with AI" button once the
// feature is re-enabled properly.
const AI_ATTACHMENT_ANALYSIS_ENABLED = false;

export interface AttachmentsManagerProps {
  onClose: () => void;
  embedded?: boolean;
  /**
   * When true, row actions change to a single "Select" action.
   * Clicking Select fires onSelect(id) and calls onClose().
   * Delete actions are hidden in pickMode.
   */
  pickMode?: boolean;
  /** Called when a file is selected in pickMode. */
  onSelect?: (attachmentId: string) => void;
}

export function AttachmentsManager({ onClose, embedded = false, pickMode = false, onSelect }: AttachmentsManagerProps) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [previewItem, setPreviewItem] = useState<any | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const [storageError, setStorageError] = React.useState<string | null>(null);

  const refreshAttachments = React.useCallback(async () => {
    try {
      setLoading(true);
      setStorageError(null);
      const attachments = await attachmentsService.list(query);
      setItems(attachments);
    } catch (error: any) {
      const msg = error?.message || '';
      if (msg.includes('storage') || msg.includes('503') || msg.includes('not configured')) {
        setStorageError('File storage is not configured. Contact your administrator to set up cloud storage.');
      } else {
        useNotificationStore.getState().error('Failed to load attachments');
      }
    } finally {
      setLoading(false);
    }
  }, [query]);

  const uploadAttachment = async (file: File, options: { title: string }) => {
    return await attachmentsService.upload(file, options);
  };

  const deleteAttachment = async (id: string) => {
    await attachmentsService.delete(id);
  };

  const analyzeAttachment = async (id: string) => {
    await attachmentsService.analyze(id);
  };

  // Load attachments on mount and when query changes
  useEffect(() => {
    refreshAttachments();
  }, [refreshAttachments]);

	const [isUploading, setUploading] = useState(false);
	const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
	const [manuallyAnalyzingIds, setManuallyAnalyzingIds] = useState<Set<string>>(new Set());

	React.useEffect(() => {
		if (!AI_ATTACHMENT_ANALYSIS_ENABLED) return;
		const itemsBeingAnalyzed = items.some(item => {
			if (item.aiAnalyzedAt) {
				if (manuallyAnalyzingIds.has(item.id)) {
					setManuallyAnalyzingIds(prev => { const next = new Set(prev); next.delete(item.id); return next; });
				}
				return false;
			}
			if (manuallyAnalyzingIds.has(item.id)) return true;
			const uploadedAt = new Date(item.metadata?.uploadedAt || item.createdAt);
			return (Date.now() - uploadedAt.getTime()) < 10 * 60 * 1000;
		});
		if (!itemsBeingAnalyzed) return;
		const timer = setInterval(() => refreshAttachments(), 5000);
		return () => clearInterval(timer);
	}, [items, manuallyAnalyzingIds, refreshAttachments]);

	const filtered = useMemo(() => {
		if (!query.trim()) return items;
		const q = query.toLowerCase();
		return items.filter(i => (i.title || i.originalFilename || '').toLowerCase().includes(q));
	}, [items, query]);

	const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputEl = e.currentTarget as HTMLInputElement | null;
		const list = e.target?.files ? Array.from(e.target.files) : [];
		if (list.length === 0) return;
		setUploading(true);
		try {
			let successCount = 0;
			let errorCount = 0;
			for (const file of list) {
				try {
					await uploadAttachment(file, { title: file.name.replace(/\.[^.]+$/, '') });
					successCount++;
				} catch (error) {
					errorCount++;
					console.error(`Failed to upload ${file.name}:`, error);
					useNotificationStore.getState().error(`Failed to upload ${file.name}`);
				}
			}
			if (successCount > 0) {
				await refreshAttachments();
				useNotificationStore.getState().success(`Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}`);
			}
			if (errorCount > 0 && successCount === 0) {
				useNotificationStore.getState().error(`Failed to upload ${errorCount} file${errorCount > 1 ? 's' : ''}`);
			}
		} finally {
			setUploading(false);
			if (inputEl) inputEl.value = '';
		}
	};

	const handleDelete = async (id: string) => {
		try { await deleteAttachment(id); await refreshAttachments(); setDeleteConfirmId(null); } catch {}
	};

	// Helpers
	const formatBytes = (bytes: number): string => {
		if (!bytes && bytes !== 0) return '-';
		const units = ['B','KB','MB','GB','TB'];
		let value = bytes;
		let unit = 0;
		while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit++; }
		return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
	};

	const getExtension = (name: string | undefined): string => {
		if (!name) return '';
		const m = /\\.([a-z0-9]+)(?:\\?.*)?$/i.exec(name);
		return (m?.[1] || '').toLowerCase();
	};

	const getFileCategory = (mime: string, name?: string): string => {
		const m = (mime || '').toLowerCase();
		const ext = getExtension(name);
		if (m.startsWith('image/')) return 'image';
		if (m.startsWith('video/')) return 'video';
		if (m.startsWith('audio/')) return 'audio';
		if (m === 'application/pdf' || ext === 'pdf') return 'pdf';
		if (m.includes('spreadsheet') || ['xls','xlsx','csv'].includes(ext)) return 'spreadsheet';
		if (m.includes('presentation') || ['ppt','pptx','key'].includes(ext)) return 'presentation';
		if (['zip','rar','7z','tar','gz'].includes(ext) || m.includes('zip') || m.includes('tar') || m.includes('gzip')) return 'archive';
		if (m.includes('json') || ['js','ts','tsx','jsx','json','html','css','py','rb','go','java','cs','php','sh','yml','yaml'].includes(ext)) return 'code';
		if (m.startsWith('text/') || ['txt','md','log'].includes(ext)) return 'text';
		if (m.includes('word') || ['doc','docx','rtf','odt','pages'].includes(ext)) return 'document';
		return 'other';
	};

	const FileTypeIcon = ({ category, className = 'w-10 h-10' }: { category: string; className?: string }) => {
		switch (category) {
			case 'pdf':
				return (
					<svg className={className + ' text-red-600'} viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7m0 0V5a2 2 0 012-2h6l4 4z"/>
						<text x="7" y="17" fontSize="7" className="fill-current">PDF</text>
					</svg>
				);
			case 'image':
				return (
					<svg className={className + ' text-emerald-600'} viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a2 2 0 012-2h12a2 2 0 012 2v14l-4-4-4 4-4-4-4 4V5z"/>
					</svg>
				);
			case 'video':
				return (
					<svg className={className + ' text-purple-600'} viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14"/>
						<rect x="3" y="6" width="12" height="12" rx="2" ry="2"/>
					</svg>
				);
			case 'audio':
				return (
					<svg className={className + ' text-indigo-600'} viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-2v13"/>
						<circle cx="6" cy="18" r="3"/>
						<circle cx="18" cy="16" r="3"/>
					</svg>
				);
			case 'spreadsheet':
				return (
					<svg className={className + ' text-green-600'} viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<rect x="4" y="4" width="16" height="16" rx="2"/>
						<path strokeWidth={2} d="M4 9h16M9 4v16"/>
					</svg>
				);
			case 'presentation':
				return (
					<svg className={className + ' text-orange-600'} viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<rect x="3" y="4" width="18" height="12" rx="2"/>
						<path strokeWidth={2} d="M12 16v4m-5 0h10"/>
					</svg>
				);
			case 'archive':
				return (
					<svg className={className + ' text-amber-600'} viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<rect x="3" y="4" width="18" height="4" rx="1"/>
						<path strokeWidth={2} d="M5 8h14v10a2 2 0 01-2 2H7a2 2 0 01-2-2V8zm7-2v2"/>
					</svg>
				);
			case 'code':
				return (
					<svg className={className + ' text-blue-600'} viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 18l6-6-6-6M8 6l-6 6 6 6"/>
					</svg>
				);
			case 'text':
			case 'document':
				return (
					<svg className={className + ' text-gray-600'} viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7m0 0V5a2 2 0 012-2h6l4 4z"/>
					</svg>
				);
			default:
				return (
					<svg className={className + ' text-gray-400'} viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a2 2 0 000-2.828z" />
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 13l3-3" />
					</svg>
				);
		}
	};

	const content = (
		<div className="flex flex-col h-full">
			<div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
				<div className="relative flex-1 w-full">
					<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
						<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
					</div>
					<Input type="text" placeholder="Search files..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-10" />
				</div>
				<input id="attachment-file-upload" type="file" multiple className="hidden" accept="*/*" onChange={handleUpload} disabled={isUploading} />
				<Button variant="primary" size="md" isLoading={isUploading} type="button" onClick={() => document.getElementById('attachment-file-upload')?.click()} className="w-full sm:w-auto">
					<svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
					{isUploading ? 'Uploading...' : 'Upload Files'}
				</Button>
				<div className="inline-flex rounded-md border overflow-hidden h-10">
					<button
						className={`px-3 py-2 text-sm h-full ${viewMode === 'grid' ? 'bg-gray-100 text-foreground' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
						onClick={() => setViewMode('grid')}
						aria-pressed={viewMode === 'grid'}
						title="Grid view"
					>
						<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h7v7H4zM13 4h7v7h-7zM13 13h7v7h-7zM4 13h7v7H4z"/></svg>
					</button>
					<button
						className={`px-3 py-2 text-sm h-full border-l ${viewMode === 'list' ? 'bg-gray-100 text-foreground' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
						onClick={() => setViewMode('list')}
						aria-pressed={viewMode === 'list'}
						title="List view"
					>
						<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/></svg>
					</button>
				</div>
			</div>
			<div className="flex-1 overflow-auto">
				{filtered.length === 0 ? (
					<div className="flex items-center justify-center h-full min-h-[400px]">
						<div className="text-center space-y-4 p-8">
							<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100">
								<svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a2 2 0 000-2.828z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 13l3-3" /></svg>
							</div>
							<div>
								<h3 className="text-base font-medium text-foreground">{query ? 'No files found' : 'No files yet'}</h3>
								<p className="mt-1 text-sm text-muted-foreground">{query ? 'Try a different search term' : 'Upload your first file to get started'}</p>
							</div>
							{!query && (
								<Button variant="primary" size="md" onClick={() => document.getElementById('attachment-file-upload')?.click()}>
									<svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
									Upload Files
								</Button>
							)}
						</div>
					</div>
				) : viewMode === 'grid' ? (
					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
							{filtered.map((item) => {
								const mime = (item.metadata?.mimeType || item.mimeType || '');
								const isImage = mime.startsWith('image/');
								const isPdf = mime === 'application/pdf' || mime.startsWith('application/pdf');
								const category = getFileCategory(mime, item.filename || item.originalFilename);
								return (
									<Card key={item.id} variant="default" padding="none" className="group hover:shadow-md transition-all duration-200">
										<div
											className="aspect-square bg-gray-50 relative overflow-hidden cursor-pointer"
											role="button"
											tabIndex={0}
											onClick={() => setPreviewItem(item)}
											onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewItem(item); } }}
											aria-label={isPdf ? 'Preview PDF' : 'Preview file'}
										>
											{isImage ? (
												<img src={item.thumbnailUrl || item.url} alt={item.title || item.originalFilename} className="w-full h-full object-cover" loading="lazy" />
											) : (
												<div className="w-full h-full flex items-center justify-center bg-gray-50 text-gray-400">
													<FileTypeIcon category={category} />
												</div>
											)}
											<a
												href={item.url}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) => e.stopPropagation()}
												className="absolute top-2 right-2 z-20 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-900/60 text-white text-xs opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
												aria-label="Open in new tab"
											>
												<svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7m0 0v7m0-7L10 14" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10v11h11" /></svg>
												Open
											</a>
											<div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
										</div>
										<CardContent className="p-3">
											<div className="mb-2">
												<SimpleTooltip content={item.title || item.originalFilename}>
													<h4 className="text-sm font-medium text-foreground truncate cursor-help">{item.title || item.originalFilename}</h4>
												</SimpleTooltip>
												{item.description ? (
													<SimpleTooltip content={item.description}><p className="text-xs text-gray-600 truncate mt-1 cursor-help">📝 {item.description}</p></SimpleTooltip>
												) : AI_ATTACHMENT_ANALYSIS_ENABLED && !item.aiAnalyzedAt && (manuallyAnalyzingIds.has(item.id) || (Date.now() - new Date(item.metadata?.uploadedAt || item.createdAt).getTime()) < 10 * 60 * 1000) ? (
													<div className="flex items-center gap-1 mt-1"><div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div><span className="text-xs text-blue-600">Analyzing...</span></div>
												) : AI_ATTACHMENT_ANALYSIS_ENABLED && !item.aiAnalyzedAt ? (
													<button onClick={async () => { setManuallyAnalyzingIds(prev => new Set(prev).add(item.id)); try { await analyzeAttachment(item.id); } catch { setManuallyAnalyzingIds(prev => { const next = new Set(prev); next.delete(item.id); return next; }); } }} className="text-xs text-blue-600 hover:text-blue-700 underline mt-1" title="Click to analyze with AI">🤖 Analyze with AI</button>
												) : null}
											</div>
											<div className="flex items-center gap-2">
												{pickMode ? (
													<Button size="sm" variant="primary" onClick={() => { onSelect?.(item.id); onClose(); }} className="h-8 px-2 flex-1" title="Select this file">
														Select
													</Button>
												) : (
													<>
														<Button size="sm" variant="ghost" onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')} className="h-8 px-2 flex-1" title="Open in new tab">
															<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7m0 0v7m0-7L10 14" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10v11h11" /></svg>
															<span className="ml-1.5">Open</span>
														</Button>
														<Button size="sm" variant="ghost" onClick={() => setDeleteConfirmId(item.id)} className="h-8 px-2 !text-red-600 hover:!text-red-700 hover:!bg-red-50 flex-shrink-0" title="Delete">
															<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
														</Button>
													</>
												)}
											</div>
										</CardContent>
									</Card>
								);
							})}
					</div>
					) : (
					<div className="bg-white rounded-lg border">
						<Table
							data={filtered as any}
							columns={[
								{ key: 'name', title: 'Name', render: (_: any, record: any) => {
									const mime = (record.metadata?.mimeType || record.mimeType || '');
									const category = getFileCategory(mime, record.filename || record.originalFilename);
									return (
										<div className="flex items-center gap-3">
											<FileTypeIcon category={category} className="w-6 h-6" />
											<button className="text-sm font-medium text-foreground hover:underline text-left" onClick={() => setPreviewItem(record)}>
												{record.title || record.originalFilename}
											</button>
										</div>
									);
								}},
								{ key: 'description', title: 'Description', render: (_: any, record: any) => (
									record.description ? (
										<SimpleTooltip content={record.description}><span className="text-sm text-gray-600 truncate inline-block max-w-[280px] align-middle">{record.description}</span></SimpleTooltip>
									) : <span className="text-sm text-muted-foreground">—</span>
								)},
								{ key: 'type', title: 'Type', render: (_: any, record: any) => {
									const mime = (record.metadata?.mimeType || record.mimeType || '');
									const category = getFileCategory(mime, record.filename || record.originalFilename);
									return <span className="text-sm text-gray-600 capitalize">{category}</span>;
								}},
								{ key: 'size', title: 'Size', render: (_: any, record: any) => <span className="text-sm text-gray-600">{formatBytes(record.metadata?.size || record.size || 0)}</span> },
								{ key: 'uploaded', title: 'Uploaded', render: (_: any, record: any) => <span className="text-sm text-gray-600">{record.metadata?.uploadedAt ? formatDateTimeStr(record.metadata.uploadedAt) : '-'}</span> },
								{ key: 'actions', title: 'Actions', render: (_: any, record: any) => (
									<TableActions>
										{pickMode ? (
											<TableActionButton icon={Eye} onClick={() => { onSelect?.(record.id); onClose(); }} title="Select this file">
												Select
											</TableActionButton>
										) : (
											<>
												<TableActionButton icon={Eye} onClick={() => setPreviewItem(record)} title="Preview">
													Preview
												</TableActionButton>
												<TableActionButton icon={ExternalLink} onClick={() => window.open(record.url, '_blank', 'noopener,noreferrer')} title="Open in new tab">
													Open
												</TableActionButton>
												<TableActionButton variant="destructive" icon={Trash2} onClick={() => setDeleteConfirmId(record.id)} title="Delete">
													Delete
												</TableActionButton>
											</>
										)}
									</TableActions>
								)}
							] as any}
							loading={loading}
							searchable={false}
						/>
					</div>
				)}
			</div>
		</div>
	);

	return (
		<>
			{embedded ? content : (
				<Modal isOpen={true} onClose={onClose} title={pickMode ? 'Select Image' : 'Attachments'} className="max-w-5xl">
					{content}
				</Modal>
			)}
			{/* Preview Modal */}
			<AttachmentPreviewModal
				item={previewItem}
				onClose={() => setPreviewItem(null)}
				onUpdate={async (data) => {
					if (!previewItem) return;
					await attachmentsService.update(previewItem.id, data);
					await refreshAttachments();
					// Update the preview item with new data
					setPreviewItem((prev: any) => prev ? { ...prev, ...data } : null);
				}}
			/>
			<Dialog isOpen={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} title="Delete Attachment" description="Are you sure you want to delete this item? This action cannot be undone." confirmText="Delete" confirmVariant="destructive" onConfirm={() => deleteConfirmId && handleDelete(deleteConfirmId)} />
		</>
	);
}

